/**
 * @fileoverview Sales CRM prospect API service.
 * @description Calls the backend's Sales CRM prospect endpoints (Confluence 3.5.11): the pipeline
 * backlog (GET /sales/prospects, every page at once), its KPI cards (GET /sales/prospects/summary),
 * one prospect's details with its ETag (GET /sales/prospects/{prospectId}), the Create Prospect
 * owner choices (GET /sales/prospects/eligible-owners), Create Prospect (POST /sales/prospects) and
 * Withdraw (POST /sales/prospects/{prospectId}/withdraw) and Assign Prospect Owner (POST
 * /sales/prospects/{prospectId}/assign-owner), Edit (PATCH /sales/prospects/{prospectId}) and
 * Archive and Restore (POST /sales/prospects/{prospectId}/archive and /restore). Every command
 * takes its `Idempotency-Key` from the caller (see useIdempotentMutation), so a retry resends the
 * same key; Withdraw, Assign Prospect Owner, Edit, Archive and Restore also send the details' ETag
 * as `If-Match`.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { MissingVersionError } from '@/features/sales/lib/request-error.utils'
import { ResultCapExceededError } from '@/features/sales/lib/result-cap.utils'
import { httpClient } from '@/shared/api/http-client'

import type {
  CreateProspectRequest,
  EditProspectRequest,
  EligibleProspectOwner,
  ProspectArchiveResult,
  ProspectCreated,
  ProspectEdited,
  ProspectDetails,
  ProspectListFilters,
  ProspectListItem,
  ProspectOwnerAssignment,
  ProspectSummary,
  ProspectWithdrawal,
  VersionedProspectDetails,
} from '@/features/sales/types/prospect.types'
import type { ResponseWithPageMeta } from '@/shared/api/pagination.utils'

// The backend's maximum page size.
const PAGE_LIMIT = 100
// The most pages read at once: 5000 rows, well above any real list.
const MAX_PAGES = 50
const MAX_ITEMS = PAGE_LIMIT * MAX_PAGES
// How many of the remaining pages are requested at the same time.
const MAX_PARALLEL_PAGES = 6

/**
 * @description Fetches one page of an `{ items }` list.
 * @param {string} url - The list endpoint.
 * @param {object} params - The list's filters.
 * @param {number} page - The page to fetch (from 1).
 * @returns {Promise<ResponseWithPageMeta<{ items: T[] }>>} The page and its paging meta.
 */
function readPage<T>(
  url: string,
  params: object,
  page: number,
): Promise<ResponseWithPageMeta<{ items: T[] }>> {
  return httpClient.get<{ items: T[] }>(url, { params: { ...params, page, limit: PAGE_LIMIT } })
}

/**
 * @description Reads every page of an `{ items }` list. Page 1 reports the total and the page size
 * the backend actually used, then the other pages are fetched a few at a time (never all at once,
 * so a small server page size cannot turn into hundreds of simultaneous requests). A list that
 * would take more than 50 pages (5000 rows) is refused with a ResultCapExceededError rather than
 * returned cut short.
 * @param {string} url - The list endpoint.
 * @param {object} params - The list's filters (page and limit are added here).
 * @returns {Promise<T[]>} Every item, in the backend's order.
 * @throws {ResultCapExceededError} When the list is longer than 50 pages.
 */
export async function readAllItems<T>(url: string, params: object): Promise<T[]> {
  const first = await readPage<T>(url, params, 1)
  const items = [...first.data.items]
  const total = first.pageMeta?.total

  if (total === undefined) {
    return readRemainingSequentially<T>(url, params, items)
  }
  if (items.length === 0 || items.length >= total) return items

  // The backend may answer with a smaller page than asked for; its reported size (else what page 1
  // actually held) decides how many pages there are.
  const pageSize = first.pageMeta?.limit || items.length
  const pageCount = Math.ceil(total / pageSize)
  if (pageCount > MAX_PAGES) throw new ResultCapExceededError(MAX_ITEMS)

  for (let first = 2; first <= pageCount; first += MAX_PARALLEL_PAGES) {
    const requests: Promise<ResponseWithPageMeta<{ items: T[] }>>[] = []
    for (let page = first; page <= Math.min(first + MAX_PARALLEL_PAGES - 1, pageCount); page += 1) {
      requests.push(readPage<T>(url, params, page))
    }
    for (const response of await Promise.all(requests)) {
      items.push(...response.data.items)
    }
  }
  return items
}

/**
 * @description Reads on page by page when the backend did not report a total, until a short page.
 * The page size is what page 1 held (the backend may clamp the size asked for), so a clamped list
 * is still read to its end instead of stopping after one page.
 * @param {string} url - The list endpoint.
 * @param {object} params - The list's filters.
 * @param {T[]} items - Page 1's items.
 * @returns {Promise<T[]>} Every item.
 * @throws {ResultCapExceededError} When the list is still going after 50 pages.
 */
async function readRemainingSequentially<T>(url: string, params: object, items: T[]): Promise<T[]> {
  const pageSize = items.length
  let lastPageLength = pageSize
  for (let page = 2; pageSize > 0 && lastPageLength === pageSize; page += 1) {
    if (page > MAX_PAGES) throw new ResultCapExceededError(MAX_ITEMS)
    const response = await readPage<T>(url, params, page)
    lastPageLength = response.data.items.length
    items.push(...response.data.items)
  }
  return items
}

/**
 * @description Builds a new idempotency key for one command.
 * @returns {string} A key the backend accepts (1-100 of [A-Za-z0-9._:-]).
 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID()
}

/**
 * @description Drops blank filters, so they're left out of the query string rather than sent empty
 * (the backend rejects a blank parameter). Shared with the approvals list.
 * @param {object} filters - The list's filters.
 * @returns {Record<string, string>} Only the filters that have a value.
 */
export function withoutBlankFilters(filters: object): Record<string, string> {
  return Object.fromEntries(
    Object.entries(filters)
      .filter(([, value]) => value !== undefined && String(value).trim() !== '')
      .map(([key, value]) => [key, String(value)]),
  )
}

/**
 * @description Fetches the whole pipeline backlog matching the filters, page by page.
 * @param {ProspectListFilters} [filters] - Search, filters and order.
 * @returns {Promise<ProspectListItem[]>} Every matching prospect, in the backend's order.
 */
export function getAllProspects(filters: ProspectListFilters = {}): Promise<ProspectListItem[]> {
  return readAllItems<ProspectListItem>('/sales/prospects', withoutBlankFilters(filters))
}

/**
 * @description Fetches the pipeline KPI cards, which ignore the table's filters.
 * @returns {Promise<ProspectSummary>} The caller's prospect, branch, hot-lead and pending counts.
 */
export async function getProspectSummary(): Promise<ProspectSummary> {
  const { data } = await httpClient.get<ProspectSummary>('/sales/prospects/summary')
  return data
}

/**
 * @description Fetches one prospect in full, with the ETag Withdraw needs.
 * @param {string} prospectId - The prospect to fetch.
 * @returns {Promise<VersionedProspectDetails>} The prospect and its version.
 */
export async function getProspectDetails(prospectId: string): Promise<VersionedProspectDetails> {
  const response = await httpClient.get<ProspectDetails>(
    `/sales/prospects/${encodeURIComponent(prospectId)}`,
  )
  const etag: unknown = response.headers.etag
  if (typeof etag !== 'string' || etag === '') {
    throw new MissingVersionError()
  }
  return { prospect: response.data, etag }
}

/**
 * @description Fetches every Sales Executive the caller may name as a new prospect's owner.
 * @returns {Promise<EligibleProspectOwner[]>} The choices, ordered by display name.
 */
export function getEligibleProspectOwners(): Promise<EligibleProspectOwner[]> {
  return readAllItems<EligibleProspectOwner>('/sales/prospects/eligible-owners', {})
}

/**
 * @description Creates a prospect: accepted at once for a senior, or submitted for review for a
 * Sales Executive.
 * @param {CreateProspectRequest} request - The wizard's five sections.
 * @param {string} idempotencyKey - The command's key; resend the same key to retry it safely.
 * @returns {Promise<ProspectCreated>} The new prospect's identity and admission state.
 */
export async function createProspect(
  request: CreateProspectRequest,
  idempotencyKey: string,
): Promise<ProspectCreated> {
  const { data } = await httpClient.post<ProspectCreated>('/sales/prospects', request, {
    headers: { 'Idempotency-Key': idempotencyKey },
  })
  return data
}

/**
 * @description Withdraws the caller's own pending submission.
 * @param {string} prospectId - The submission to withdraw.
 * @param {string} etag - Its details' ETag, sent as If-Match.
 * @param {string | null} reasonNote - An optional note.
 * @param {string} idempotencyKey - The command's key; resend the same key to retry it safely.
 * @returns {Promise<ProspectWithdrawal>} The submission's new state.
 */
export async function withdrawProspect(
  prospectId: string,
  etag: string,
  reasonNote: string | null,
  idempotencyKey: string,
): Promise<ProspectWithdrawal> {
  const { data } = await httpClient.post<ProspectWithdrawal>(
    `/sales/prospects/${encodeURIComponent(prospectId)}/withdraw`,
    reasonNote ? { reasonNote } : {},
    { headers: { 'If-Match': etag, 'Idempotency-Key': idempotencyKey } },
  )
  return data
}

/**
 * @description Assigns or reassigns an accepted prospect's owner (a senior only).
 * @param {string} prospectId - The prospect.
 * @param {string} etag - Its details' ETag, sent as If-Match.
 * @param {string} ownerUserId - The Sales Executive to make its owner.
 * @param {string} idempotencyKey - The command's key; resend the same key to retry it safely.
 * @returns {Promise<ProspectOwnerAssignment>} The prospect's owner now and before.
 */
export async function assignProspectOwner(
  prospectId: string,
  etag: string,
  ownerUserId: string,
  idempotencyKey: string,
): Promise<ProspectOwnerAssignment> {
  const { data } = await httpClient.post<ProspectOwnerAssignment>(
    `/sales/prospects/${encodeURIComponent(prospectId)}/assign-owner`,
    { ownerUserId },
    { headers: { 'If-Match': etag, 'Idempotency-Key': idempotencyKey } },
  )
  return data
}

/**
 * @description Edits a prospect: a Super Admin or Sales Head edits an accepted one in place; a
 * Sales Manager or Executive edits their own pending submission, which opens a new review request.
 * @param {string} prospectId - The prospect.
 * @param {string} etag - Its details' ETag, sent as If-Match.
 * @param {EditProspectRequest} request - The five sections.
 * @param {string} idempotencyKey - The command's key; resend the same key to retry it safely.
 * @returns {Promise<ProspectEdited>} The prospect's state and, for a submission, its new revision.
 */
export async function editProspect(
  prospectId: string,
  etag: string,
  request: EditProspectRequest,
  idempotencyKey: string,
): Promise<ProspectEdited> {
  const { data } = await httpClient.patch<ProspectEdited>(
    `/sales/prospects/${encodeURIComponent(prospectId)}`,
    request,
    { headers: { 'If-Match': etag, 'Idempotency-Key': idempotencyKey } },
  )
  return data
}

/**
 * @description Drops (archives) an accepted prospect with the reason why (Super Admin and Sales
 * Head only; refused while the prospect has an open target).
 * @param {string} prospectId - The prospect.
 * @param {string} etag - Its details' ETag, sent as If-Match.
 * @param {string} reasonNote - Why, 1-500 characters.
 * @param {string} idempotencyKey - The command's key; resend the same key to retry it safely.
 * @returns {Promise<ProspectArchiveResult>} The prospect's archive state.
 */
export async function archiveProspect(
  prospectId: string,
  etag: string,
  reasonNote: string,
  idempotencyKey: string,
): Promise<ProspectArchiveResult> {
  const { data } = await httpClient.post<ProspectArchiveResult>(
    `/sales/prospects/${encodeURIComponent(prospectId)}/archive`,
    { reasonNote },
    { headers: { 'If-Match': etag, 'Idempotency-Key': idempotencyKey } },
  )
  return data
}

/**
 * @description Restores a dropped prospect (Super Admin only). The note is optional and, when
 * sent, only validated: the backend never stores or publishes it.
 * @param {string} prospectId - The prospect.
 * @param {string} etag - Its details' ETag, sent as If-Match.
 * @param {string | null} reasonNote - An optional note, 1-500 characters.
 * @param {string} idempotencyKey - The command's key; resend the same key to retry it safely.
 * @returns {Promise<ProspectArchiveResult>} The prospect's archive state.
 */
export async function restoreProspect(
  prospectId: string,
  etag: string,
  reasonNote: string | null,
  idempotencyKey: string,
): Promise<ProspectArchiveResult> {
  const { data } = await httpClient.post<ProspectArchiveResult>(
    `/sales/prospects/${encodeURIComponent(prospectId)}/restore`,
    reasonNote ? { reasonNote } : {},
    { headers: { 'If-Match': etag, 'Idempotency-Key': idempotencyKey } },
  )
  return data
}
